import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, TokenResponse, LoginRequest } from '@/types';
import { authApi } from '@/services/api';
import router from '@/router';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const userRole = computed(() => user.value?.role);
  const userName = computed(() => 
    user.value ? `${user.value.firstName} ${user.value.lastName}` : ''
  );
  const userInitials = computed(() => {
    if (!user.value) return '';
    return `${user.value.firstName[0]}${user.value.lastName[0]}`.toUpperCase();
  });

  // Actions
  async function login(credentials: LoginRequest): Promise<boolean> {
    loading.value = true;
    error.value = null;
    
    try {
      const response: TokenResponse = await authApi.login(credentials);
      
      accessToken.value = response.accessToken;
      refreshToken.value = response.refreshToken;
      user.value = response.user;
      
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al iniciar sesión';
      return false;
    } finally {
      loading.value = false;
    }
  }

    async function logout(): Promise<void> {
    try {
      if (accessToken.value) {
        await authApi.logout();
      }
    } catch (err) {
      console.error('Error during logout:', err);
    } finally {
      clearAuth();
      router.push('/login');
    }
  }

  async function refreshAccessToken(): Promise<boolean> {
    if (!refreshToken.value) {
      clearAuth();
      return false;
    }

    try {
      const response: TokenResponse = await authApi.refresh(refreshToken.value);
      
      accessToken.value = response.accessToken;
      refreshToken.value = response.refreshToken;
      user.value = response.user;
      
      return true;
    } catch (err) {
      clearAuth();
      return false;
    }
  }

  function clearAuth(): void {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    error.value = null;
  }

  function hasRole(roles: string[]): boolean {
    if (!user.value) return false;
    // El super admin tiene acceso a todo (consistente con el RolesGuard del backend).
    if (user.value.role === 'super_admin') return true;
    return roles.includes(user.value.role);
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    loading,
    error,
    // Getters
    isAuthenticated,
    userRole,
    userName,
    userInitials,
    // Actions
    login,
    logout,
    refreshAccessToken,
    clearAuth,
    hasRole,
  };
}, {
  persist: {
    key: 'auth',
    // sessionStorage reduce la exposición frente a localStorage (no persiste al cerrar
    // la pestaña). Para máxima seguridad, migrar el refresh token a cookie httpOnly
    // (requiere set-cookie en backend + CSRF); ver B8 en el backlog.
    storage: sessionStorage,
    paths: ['user', 'accessToken', 'refreshToken'],
  },
});
